import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import { Button } from 'react-bootstrap';
import Datasheet from './Datasheet';
import { useBuoyDatasheetBuilder } from './BuoyBuilder';
import { useFloodDatasheetBuilder } from './FloodBuilder';
import { useUsgsDatasheetBuilder } from './UsgsBuilder';

export { default as Datasheet } from './Datasheet';
export { useBuoyDatasheetBuilder } from './BuoyBuilder';
export { useFloodDatasheetBuilder } from './FloodBuilder';
export { useUsgsDatasheetBuilder } from './UsgsBuilder';

/** Records fetched for each "Load more" — the sheet shows one at a time. */
export const DATASHEET_PAGE_SIZE = 100;

/**
 * The page load gives a device ONE row (`data[0]`). A datasheet pages through a
 * series, so it fetches its own device's window when it opens — that device
 * only, `DATASHEET_PAGE_SIZE` records at a time, in the page's `startTime` /
 * `endTime` (the last 24 hours when the mount point has no range). The fetched
 * points merge into the device cache, so `records` stays `device.data`.
 *
 * `fetchSeries: false` mounts the sheet without a fetch — for a caller that
 * shows one already-held record and wants no series, and for a caller that keeps
 * the builder mounted while the sheet is closed (pass the open flag, so selecting
 * a device elsewhere on the page does not fetch a series nobody is reading).
 *
 * A failed page is not an empty series: the fetch's `error` reaches the sheet as
 * `errorMessage`, which keeps the records already held and offers a retry.
 *
 * @param {{
 *   type?: string | null,
 *   device?: object | null,
 *   onClose?: () => void,
 *   onSettingsOpen?: import('react').MouseEventHandler<HTMLButtonElement>,
 *   footer?: import('react').ReactNode,
 *   startTime?: number,
 *   endTime?: number,
 *   fetchSeries?: boolean,
 * }} options
 */
export const useDeviceDatasheetBuilder = ({
  type,
  device,
  onClose,
  onSettingsOpen,
  footer,
  startTime,
  endTime,
  fetchSeries = true,
}) => {
  const deviceId = device?.id;
  const { loadMore, hasMore, isFetching, error } = useDeviceSeriesData({
    ids: deviceId != null ? [deviceId] : [],
    startTime,
    endTime,
    limit: DATASHEET_PAGE_SIZE,
    enabled: fetchSeries && deviceId != null,
  });

  // A failed page is not an empty series. The builders pass the API's own message
  // to the sheet as `errorMessage`, so the viewer reads the reason and can retry
  // instead of the "no data for this device" copy.
  const builderArgs = {
    device,
    onClose,
    onSettingsOpen,
    footer,
    loadMore,
    hasMore,
    isFetching,
    error,
  };
  const floodProps = useFloodDatasheetBuilder(builderArgs);
  const buoyProps = useBuoyDatasheetBuilder(builderArgs);
  const usgsProps = useUsgsDatasheetBuilder(builderArgs);

  if (type === 'usgs') return usgsProps;
  if (type === 'buoy') return buoyProps;
  return floodProps;
};

export const ViewDeviceFooter = ({ onViewDevice }) =>
  onViewDevice ? (
    <Button
      variant="primary"
      className="connected-devices-view-device-button w-100 px-3 py-2"
      onClick={onViewDevice}
    >
      View device
    </Button>
  ) : null;

export default Datasheet;
